import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, RefreshCw, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import { adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminSyncShopifyProducts } from '@/lib/api';
import { toast } from 'sonner';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    product_name: '',
    shopify_handle: '',
    shopify_product_id: '',
    brand: 'Standard Process',
    vendor: '',
    active: true,
    notes: '',
    image_url: '',
    price: 0,
    description: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = products.filter(p => 
        p.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.shopify_handle?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchQuery, products]);

  const loadProducts = async () => {
    try {
      const data = await adminGetProducts();
      setProducts(data || []);
      setFilteredProducts(data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData({
      product_name: '',
      shopify_handle: '',
      shopify_product_id: '',
      brand: 'Standard Process',
      active: true,
      notes: '',
      image_url: '',
      price: 0,
      description: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      product_name: product.product_name,
      shopify_handle: product.shopify_handle || '',
      shopify_product_id: product.shopify_product_id || '',
      brand: product.brand || 'Standard Process',
      active: product.active !== false,
      notes: product.notes || '',
      image_url: product.image_url || '',
      price: product.price || 0,
      description: product.description || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price) || 0 };
      if (editingProduct) {
        await adminUpdateProduct(editingProduct.id, data);
        toast.success('Product updated');
      } else {
        await adminCreateProduct(data);
        toast.success('Product created');
      }
      setDialogOpen(false);
      loadProducts();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.product_name}"?`)) return;
    try {
      await adminDeleteProduct(product.id);
      toast.success('Product deleted');
      loadProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleActive = async (product) => {
    try {
      await adminUpdateProduct(product.id, { active: !product.active });
      loadProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleSyncShopify = async () => {
    setSyncing(true);
    try {
      const result = await adminSyncShopifyProducts();
      toast.success(`Sync complete: ${result.total_shopify_products} products found, ${result.created} new, ${result.updated} updated`);
      loadProducts();
    } catch (error) {
      toast.error('Failed to sync from Shopify');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading products...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-[#1a1a1a] mb-1">Products</h1>
          <p className="text-gray-600 text-sm">{products.length} products in database</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleSyncShopify} disabled={syncing} variant="outline" className="btn-outline" data-testid="sync-shopify-btn">
            {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Store className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {syncing ? 'Syncing...' : 'Sync from Shopify'}
          </Button>
          <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Add Product
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search products..."
          className="pl-10"
          data-testid="product-search-input"
        />
      </div>

      {filteredProducts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">
              {searchQuery ? 'No products match your search' : 'No products created yet'}
            </p>
            {!searchQuery && (
              <Button onClick={openCreateDialog} variant="outline" className="btn-outline">
                Add Your First Product
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Shopify Handle</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt="" 
                          className="w-10 h-10 rounded object-cover bg-stone-100"
                        />
                      )}
                      <div>
                        <p className="font-medium text-[#2C2C2C]">{product.product_name}</p>
                        <p className="text-xs text-stone-500">{product.brand}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {product.shopify_handle ? (
                      <code className="text-xs bg-stone-100 px-2 py-1 rounded">
                        {product.shopify_handle}
                      </code>
                    ) : (
                      <span className="text-stone-400 text-sm">Not set</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {product.price > 0 ? `$${product.price.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={product.active !== false}
                      onCheckedChange={() => toggleActive(product)}
                      data-testid={`product-active-${product.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(product)}
                        className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(product)}
                        className="h-8 w-8 text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Product Name *</label>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                  placeholder="e.g., Zypan"
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Shopify Handle</label>
                  <Input
                    value={formData.shopify_handle}
                    onChange={(e) => setFormData({ ...formData, shopify_handle: e.target.value })}
                    placeholder="e.g., zypan-90-tabs"
                    data-testid="product-handle-input"
                  />
                </div>
                <div>
                  <label className="form-label">Shopify Product ID</label>
                  <Input
                    value={formData.shopify_product_id}
                    onChange={(e) => setFormData({ ...formData, shopify_product_id: e.target.value })}
                    placeholder="e.g., 123456789"
                    data-testid="product-shopify-id-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Brand</label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Standard Process"
                    data-testid="product-brand-input"
                  />
                </div>
                <div>
                  <label className="form-label">Price ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    data-testid="product-price-input"
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Image URL</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-image-input"
                />
              </div>
              <div>
                <label className="form-label">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief product description..."
                  rows={2}
                  data-testid="product-description-input"
                />
              </div>
              <div>
                <label className="form-label">Notes (Internal)</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes..."
                  rows={2}
                  data-testid="product-notes-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="product-active-input"
                />
                <label className="text-sm text-stone-600">Active (visible in recommendations)</label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-product-btn">
                {editingProduct ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
